// ============================================================================
// 📁 File: src/modules/ppe/pages/engineering/drawing/DrawingListPage.jsx
// 🧭 Module: PPE → Engineering → Drawing Master
// 📝 Pattern:
//   - SAME AS ProductVariantListPage
//   - HTML table (Bootstrap)
//   - Inline actions
// ============================================================================

import React, { useEffect, useState, useRef } from "react";
import { Card, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import axiosClient from "../../../../../api/axiosClient";

import DrawingKpiCards from "../../../components/engineering/drawing/DrawingKpiCards";
import DrawingDrawer from "./DrawingDrawer";

// ============================================================================
// COMPONENT
// ============================================================================

const DrawingListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [kpis, setKpis] = useState({
    total: 0,
    draft: 0,
    released: 0,
    obsolete: 0,
    inactive: 0,
    with_file: 0,
    without_file: 0,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const fileInputRef = useRef(null);
  const uploadRecordRef = useRef(null);

  // --------------------------------------------------------------------------
  // LOADERS
  // --------------------------------------------------------------------------

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/api/ppe/masters/drawings");
      setRows(res.data?.data || []);
    } catch {
      message.error("Failed to load drawings");
    } finally {
      setLoading(false);
    }
  };

  const loadKpis = async () => {
    try {
      const res = await axiosClient.get("/api/ppe/masters/drawings/kpis");
      setKpis(res.data?.data || {});
    } catch {
      message.error("Failed to load drawing KPIs");
    }
  };

  const reloadAll = async () => {
    await Promise.all([loadList(), loadKpis()]);
  };

  // --------------------------------------------------------------------------
  // LIFECYCLE
  // --------------------------------------------------------------------------

  useEffect(() => {
    reloadAll();
  }, []);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  const handleCreate = () => {
    setEditingRecord(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingRecord(null);
  };

  // --------------------------------------------------------------------------
  // FILE / STATUS ACTIONS
  // --------------------------------------------------------------------------

  const triggerUpload = (record) => {
    uploadRecordRef.current = record;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadRecordRef.current) return;

    const record = uploadRecordRef.current;

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axiosClient.post(
        `/api/ppe/masters/drawings/${record.id}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Drawing uploaded");
      reloadAll();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Upload failed"
      );
    } finally {
      e.target.value = "";
      uploadRecordRef.current = null;
    }
  };

  const handleDownload = async (record) => {
  try {
    const res = await axiosClient.get(
      `/api/ppe/masters/drawings/${record.id}/download`,
      { responseType: "blob" }
    );

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;

    const filename =
      record.file_name?.split("_").slice(-1).join("_") ||
      "drawing-file";

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch {
    message.error("Download failed");
  }
};

  const handleRelease = async (record) => {
    await axiosClient.post(
      `/api/ppe/masters/drawings/${record.id}/release`
    );
    message.success("Drawing released");
    reloadAll();
  };

  const handleObsolete = async (record) => {
    await axiosClient.post(
      `/api/ppe/masters/drawings/${record.id}/obsolete`
    );
    message.success("Drawing marked obsolete");
    reloadAll();
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <>
      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Drawing Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Engineering drawings & lifecycle
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Drawing
        </Button>
      </div>

      {/* KPI CARDS */}
      <DrawingKpiCards data={kpis} />

      {/* TABLE */}
      <Card>
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Drawing No</th>
                <th>Revision</th>
                <th>Title</th>
                <th>Status</th>
                <th>File</th>
                <th style={{ width: 260 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No drawings found
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.drawing_no}</td>
                  <td>{r.revision}</td>
                  <td>{r.title}</td>

                  <td>
                    <span
                      className={`badge ${
                        r.status_code === "DRAFT"
                          ? "bg-warning"
                          : r.status_code === "RELEASED"
                          ? "bg-success"
                          : r.status_code === "OBSOLETE"
                          ? "bg-danger"
                          : "bg-secondary"
                      }`}
                    >
                      {r.status_code}
                    </span>
                  </td>

                  <td style={{ maxWidth: 320 }}>
  {r.file_name ? (
    <span
      title={r.file_name}
      style={{
        display: "inline-block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 300,
      }}
    >
      {`${r.drawing_no} – Rev ${r.revision} (${r.file_name
        .split("_")
        .slice(-1)
        .join("_")})`}
    </span>
  ) : (
    "-"
  )}
</td>


                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(r)}
                      >
                        Edit
                      </button>

                      {r.status_code === "DRAFT" && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => triggerUpload(r)}
                        >
                          Upload
                        </button>
                      )}

                      {r.file_name && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleDownload(r)}
                        >
                          Download
                        </button>
                      )}

                      {r.status_code === "DRAFT" && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleRelease(r)}
                        >
                          Release
                        </button>
                      )}

                      {r.status_code === "RELEASED" && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleObsolete(r)}
                        >
                          Obsolete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.dwg,.dxf"
        onChange={handleFileSelected}
      />

      {/* DRAWER */}
      <DrawingDrawer
        open={drawerOpen}
        record={editingRecord}
        onClose={handleDrawerClose}
        onSuccess={reloadAll}
      />
    </>
  );
};

export default DrawingListPage;
